/** Judge.me via Intan API (private key on server). Uses HTTP + memory cache. */

export interface JudgeMeBadge {
  averageRating: number;
  numberOfReviews: number;
  numberOfQuestions: number;
}

export interface JudgeMeReview {
  id: string;
  author: string;
  title: string;
  body: string;
  rating: number;
  createdAt: string | null;
  verified?: boolean;
  pictures: string[];
}

export interface JudgeMeProductReviews {
  badge: JudgeMeBadge;
  reviews: JudgeMeReview[];
}

const MEMORY_TTL_MS = 5 * 60 * 1000;

type Mem<T> = { at: number; value: T };
const badgeMem = new Map<string, Mem<JudgeMeBadge | null>>();
const reviewsMem = new Map<string, Mem<JudgeMeProductReviews | null>>();

function memGet<T>(map: Map<string, Mem<T>>, key: string): T | undefined {
  const hit = map.get(key);
  if (!hit) return undefined;
  if (Date.now() - hit.at > MEMORY_TTL_MS) {
    map.delete(key);
    return undefined;
  }
  return hit.value;
}

function memSet<T>(map: Map<string, Mem<T>>, key: string, value: T) {
  map.set(key, { at: Date.now(), value });
}

async function fetchJson(url: string): Promise<any | null> {
  try {
    const res = await fetch(url, { cache: 'default' });
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

export async function fetchJudgeMeBadge(
  apiUrl: string,
  productId: string
): Promise<JudgeMeBadge | null> {
  const key = `${apiUrl}|${productId}`;
  const cached = memGet(badgeMem, key);
  if (cached !== undefined) return cached;

  // Prefer full reviews payload (same redis key family) so badge + list share one trip when possible
  const full = await fetchJudgeMeProductReviews(apiUrl, productId);
  if (full) {
    memSet(badgeMem, key, full.badge);
    return full.badge;
  }

  const json = await fetchJson(
    `${apiUrl}/live/judgeme/badge?product_id=${encodeURIComponent(productId)}`
  );
  const badge = (json?.data as JudgeMeBadge) || null;
  memSet(badgeMem, key, badge);
  return badge;
}

export async function fetchJudgeMeProductReviews(
  apiUrl: string,
  productId: string
): Promise<JudgeMeProductReviews | null> {
  const key = `${apiUrl}|${productId}`;
  const cached = memGet(reviewsMem, key);
  if (cached !== undefined) return cached;

  const json = await fetchJson(
    `${apiUrl}/live/judgeme/reviews?product_id=${encodeURIComponent(productId)}`
  );
  const data = json?.data as JudgeMeProductReviews | undefined;
  if (!data || !Array.isArray(data.reviews)) {
    memSet(reviewsMem, key, null);
    return null;
  }
  const normalized: JudgeMeProductReviews = {
    badge: data.badge || {
      averageRating: 0,
      numberOfReviews: data.reviews.length,
      numberOfQuestions: 0,
    },
    reviews: data.reviews.map((r) => ({
      ...r,
      pictures: Array.isArray(r.pictures) ? r.pictures : [],
    })),
  };
  memSet(reviewsMem, key, normalized);
  memSet(badgeMem, key, normalized.badge);
  return normalized;
}

/** @deprecated use fetchJudgeMeProductReviews — kept for call sites that paginate */
export async function fetchJudgeMeReviews(
  apiUrl: string,
  productId: string,
  _perPage = 20,
  _page = 1
): Promise<JudgeMeReview[]> {
  const data = await fetchJudgeMeProductReviews(apiUrl, productId);
  return data?.reviews || [];
}
